import {
  generatedApi,
  GetAssessmentQuestionsResponseDto,
  SubmitAssessmentResponseDto,
  AssessmentAnswerDto,
} from "./generated";

// Public (tokenized-link) UK assessment endpoints. Hand-injected because the
// RTK codegen (`npm run generate`) hasn't been re-run against the updated
// backend swagger yet — these match the backend uk-onboarding.controller
// public routes and can be removed once codegen picks them up.
const apiWithPublicUkAssessment = generatedApi.injectEndpoints({
  endpoints: (build) => ({
    validatePublicUkAssessmentToken: build.mutation<
      { valid: boolean; message?: string; firstName?: string },
      { code: string }
    >({
      query: (body) => ({
        url: `/onboarding/uk/public/assessment/validate-token`,
        method: "POST",
        body,
      }),
    }),
    getPublicUkAssessmentQuestions: build.mutation<
      GetAssessmentQuestionsResponseDto,
      { code: string }
    >({
      query: (body) => ({
        url: `/onboarding/uk/public/assessment/questions`,
        method: "POST",
        body,
      }),
    }),
    submitPublicUkAssessment: build.mutation<
      SubmitAssessmentResponseDto,
      {
        code: string;
        answers: AssessmentAnswerDto[];
        investorType: "RESTRICTED" | "HIGH_NET_WORTH" | "NEITHER";
        statementAgreed: boolean;
      }
    >({
      query: (body) => ({
        url: `/onboarding/uk/public/assessment/submit`,
        method: "POST",
        body,
      }),
    }),
  }),
});

const api = apiWithPublicUkAssessment.enhanceEndpoints({
  // Add endpoint enhancements here
  addTagTypes: [
    "CryptoAddresses",
    "getDcaStrategiesTag",
    "getUserDataTag",
    "UkOnboarding",
    "todayDisclosureAcceptanceTag",
    "TaxTransactions",
    "TaxAccount",
  ],
  endpoints: {
    // The withdrawal modal picks the TrBackfill stage from this list; the
    // Travel-Rule PATCH (notabene.ts updateAddressTrFields) invalidates it so
    // a second withdrawal in the same session sees `collected`, not the
    // stale `requires_backfill` that 422s on re-submit.
    getUserCryptoAddresses: { providesTags: ["CryptoAddresses"] },
    getDcaStrategies: {
      providesTags: ["getDcaStrategiesTag"],
    },
    // Cost basis is joined server-side on getTaxTransactions; lot mutations require refetch.
    getTaxTransactions: { providesTags: ["TaxTransactions"] },
    getTaxAccount: { providesTags: ["TaxAccount"] },
    createTaxTransferLots: { invalidatesTags: ["TaxTransactions"] },
    deleteTaxTransferLots: { invalidatesTags: ["TaxTransactions"] },
    // The mutation returns the account as read back from TaxBit, so patch the cache from it.
    updateTaxAccount: {
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          apiWithPublicUkAssessment.util.updateQueryData("getTaxAccount", undefined, () => data),
        );
      },
    },
    getUserDatav2: {
      providesTags: ["getUserDataTag"],
    },
    getTodayDisclosureAcceptance: {
      providesTags: ["todayDisclosureAcceptanceTag"],
    },
    // Invalidate user data so refetched user includes new state/deposit disclosures (avoids disclosure showing again on navigate back)
    acceptStateDisclosure: { invalidatesTags: ["getUserDataTag"] },
    acceptDepositDisclosure: { invalidatesTags: ["getUserDataTag"] },
    // Invalidate today's acceptance after submit so UI knows not to show disclosure again
    createCt: { invalidatesTags: ["todayDisclosureAcceptanceTag"] },
    createTargetOrderCt: { invalidatesTags: ["todayDisclosureAcceptanceTag"] },
    createDcaStrategy: { invalidatesTags: ["todayDisclosureAcceptanceTag"] },
    processWithdrawal: { invalidatesTags: ["todayDisclosureAcceptanceTag"] },
    processFiatWithdrawal: { invalidatesTags: ["todayDisclosureAcceptanceTag"] },
    // UK Onboarding cache tags
    getUkOnboardingStatus: {
      providesTags: ["UkOnboarding"],
    },
    submitUkAccountType: {
      invalidatesTags: ["UkOnboarding"],
    },
    restartUkOnboarding: {
      invalidatesTags: ["UkOnboarding"],
    },
    markRiskSummaryViewed: {
      invalidatesTags: ["UkOnboarding"],
    },
    submitInvestorType: {
      invalidatesTags: ["UkOnboarding"],
    },
    submitRestrictedInvestorStatement: {
      invalidatesTags: ["UkOnboarding"],
    },
    submitHnwInvestorStatement: {
      invalidatesTags: ["UkOnboarding"],
    },
    submitAssessment: {
      invalidatesTags: ["UkOnboarding"],
    },
    confirmAccount: {
      invalidatesTags: ["UkOnboarding"],
    },
    declineAccount: {
      invalidatesTags: ["UkOnboarding"],
    },
  },
});

export default api;
